<script lang="ts" setup>
import { api } from '@/api';
import {
  type DataTypeInfoDto,
  type DefinitionDto,
  DefinitionParameterValueSetDto,
  EvaluationParameterLimitType,
  EvaluationType,
  GetNumericEvaluationParameterTemplateDtoTypeEnum,
  type NumericEvaluationParameterDto,
  type NumericEvaluationParameterTemplateDto,
  QuantityValueType,
} from '@/api/generator';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import VeeNumberField from '@/components/forms/NumberField/VeeNumberField.vue';
import VeeSelect from '@/components/forms/Select/VeeSelect.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils';
import { translateEntityProperty } from '@/utils/helper';
import {
  evaluationTypeOptions,
  decimalsOptions,
} from '@/views/quantities/components/quantityFormHelpers';
import Range from '@/views/quantities/components/Range.vue';
import { NIL } from 'uuid';
import { computed, onMounted, ref, watch, watchEffect } from 'vue';
import EnumOrQuantityLabel from './EnumOrQuantityLabel.vue';
import ParameterSetTable from './ParameterSetTable.vue';
import { useTranslation } from 'i18next-vue';
import { checkLimits } from '../utils';

import { useLoading } from '@/utils/useLoading';
import Loader from '@/components/Loader/Loader.vue';
import { useProtocolsStore } from '@/stores/protocolsStore';
import InfoMessage from '@/components/InfoMessage/InfoMessage.vue';
import { isNil } from 'lodash';

const protocolsStore = useProtocolsStore();
const { t } = useTranslation();

const DEFAULT_DECIMAL_PLACES: Record<QuantityValueType, number> = {
  IntegerNumber: 0,
  FloatingPoint: 3,
};

interface EvaluationsManualTasksModalScreen02Props {
  dataTypeInfoDto?: DataTypeInfoDto;
  dtoToEdit?: NumericEvaluationParameterDto;
  slideActive?: boolean;
}
const props = defineProps<EvaluationsManualTasksModalScreen02Props>();

const emit = defineEmits<{
  changeNumericEvaluationParameterDto: [param: NumericEvaluationParameterDto];
  validationStateChange: [isValid: boolean];
}>();

const {
  form: { values, setFieldValue, resetForm, setFieldError, meta, resetField },
} = useVeeForm<NumericEvaluationParameterDto>({
  mode: 'add',
  schemaName: 'NumericEvaluationParameterDto',
  options: {
    keepValuesOnUnmount: false,
    ...(props.dtoToEdit !== undefined && { initialValues: props.dtoToEdit }),
  },
});

const numericEvaluationParameterTemplateDto = ref<NumericEvaluationParameterTemplateDto>();

onMounted(async () => {
  await loadData();
  await protocolsStore.initialize();
});

watch(
  () => props.slideActive,
  (value) => {
    if (!value) {
      resetForm();
    } else {
      loadData();
    }
  },
);

const watchedValuesForBackendRequest = computed(() => ({
  definitionId: values.definition?.id,
  evaluationType: values.evaluationType,
  limitType: values.limitType,
}));

const watchedValuesForParentUpdate = computed(() => ({
  decimalPlaces: values.decimalPlaces,
  min: values.min,
  max: values.max,
  definitionParameterValueSet: values.definitionParameterValueSet,
  definitionId: values.definition?.id,
  evaluationType: values.evaluationType,
  limitType: values.limitType,
}));

watch(
  () => values.definition?.id,
  () => {
    setFieldValue('definitionParameterValueSet', undefined);
  },
);

function resetFields() {
  resetField('lowerFailedLimit', { value: undefined });

  resetField('lowerWarningLimit', { value: undefined });

  resetField('upperWarningLimit', { value: undefined });

  resetField('upperFailedLimit', { value: undefined });
}

watch(
  () => values.evaluationType,
  () => {
    // Reset the limit fields when the evaluation type changes
    resetFields();
  },
);

/** watch the form values */
watch(watchedValuesForBackendRequest, () => {
  if (!values.definition?.id || loading.value) return;

  loadData(values.definition.id, values.evaluationType ?? undefined, values.limitType ?? undefined);
});

watch(watchedValuesForParentUpdate, () => {
  emit('changeNumericEvaluationParameterDto', values);
});

watch(
  () => meta.value.valid,
  (isValid) => {
    emit('validationStateChange', isValid);
  },
  { immediate: true },
);

function getRequestParameters(
  definitionId?: string | null,
  evaluationType?: EvaluationType,
  limitType?: EvaluationParameterLimitType,
): {
  quantityId: string;
  geometryId: number | null;
  definitionId?: string | null;
  evaluationType?: EvaluationType;
  limitType?: EvaluationParameterLimitType;
} | null {
  if (props.dataTypeInfoDto?.id) {
    return {
      quantityId: props.dataTypeInfoDto.id,
      geometryId: props.dataTypeInfoDto.geometry?.id ?? null,
      definitionId,
      evaluationType,
      limitType,
    };
  }

  if (props.dtoToEdit?.dataTypeId) {
    return {
      quantityId: props.dtoToEdit.dataTypeId ?? NIL,
      geometryId: props.dtoToEdit.geometry?.id ?? null,
      definitionId: values.definition?.id ?? props.dtoToEdit.definition?.id,
      evaluationType: values.evaluationType ?? props.dtoToEdit.evaluationType ?? undefined,
      limitType: values.limitType ?? props.dtoToEdit.limitType ?? undefined,
    };
  }

  return null;
}

function updateFormFields(myResult: NumericEvaluationParameterTemplateDto) {
  const param = myResult.numericEvaluationParameter;

  setFieldValue('evaluationType', param?.evaluationType);
  setFieldValue('$type', param?.$type);
  setFieldValue('allowRepetition', param?.allowRepetition);
  setFieldValue('dataTypeId', param?.dataTypeId);
  setFieldValue('geometry', param?.geometry);
  setFieldValue('id', props.dtoToEdit?.id ?? NIL);
  setFieldValue('iuid', param?.iuid);
  setFieldValue('name', param?.name);
  setFieldValue('origin', param?.origin);
  setFieldValue('isEnabled', param?.isEnabled);
  setFieldValue('evaluationUnitAbbreviation', param?.evaluationUnitAbbreviation);

  if (isNil(values.decimalPlaces)) {
    setFieldValue(
      'decimalPlaces',
      props.dtoToEdit?.decimalPlaces ??
        param?.decimalPlaces ??
        (values.definition?.valueType && DEFAULT_DECIMAL_PLACES[values.definition.valueType]),
    );
  }

  if (!values.limitType) {
    setFieldValue('limitType', props.dtoToEdit?.limitType ?? undefined);
  }

  if (isNil(values.min)) {
    setFieldValue('min', props.dtoToEdit?.min ?? undefined);
  }

  if (isNil(values.max)) {
    setFieldValue('max', props.dtoToEdit?.max ?? undefined);
  }

  if (!values.definitionParameterValueSet) {
    const valueSets = param?.definition?.definitionParameterValueSets;
    setFieldValue('definitionParameterValueSet', valueSets?.length ? valueSets[0] : undefined);
  }
}

const {
  action: loadData,
  loadingFirstTime,
  loading,
} = useLoading(
  async (
    definitionId?: string | null,
    evaluationType?: EvaluationType,
    limitType?: EvaluationParameterLimitType,
  ) => {
    const params = getRequestParameters(definitionId, evaluationType, limitType);
    if (!params) return;

    const result = await api.taskItemsApi.getNumericEvaluationParameterTemplateForManualTask({
      getNumericEvaluationParameterTemplateDto: {
        ...params,
        $type:
          GetNumericEvaluationParameterTemplateDtoTypeEnum.PtwPtw100AppZeroApplicationSharedTaskItemComponentDtoGetNumericEvaluationParameterTemplateDtoPtwPtw100AppZeroApplicationShared,
      },
    });

    const myResult = result.result;
    if (!myResult) return;

    const areDefinitionsMatching = myResult.availableDefinitions?.every(
      (def, index) =>
        def.id === numericEvaluationParameterTemplateDto.value?.availableDefinitions?.[index]?.id,
    );

    numericEvaluationParameterTemplateDto.value =
      !areDefinitionsMatching || props.dtoToEdit
        ? myResult
        : {
            ...myResult,
            availableDefinitions: numericEvaluationParameterTemplateDto.value?.availableDefinitions,
          };

    updateFormFields(myResult);
  },
);

function getDefinitonName(d: DefinitionDto): string {
  const translated = translateEntityProperty('name', d);

  if (translated) {
    return translated;
  }
  return '';
}

const enableEntireForm = computed(() => {
  const rv =
    !!values.definitionParameterValueSet ||
    !values.definition?.definitionParameterValueSets?.length;

  return rv;
});

const computedValueType = computed<QuantityValueType>(() => {
  return values.definition?.valueType === 'IntegerNumber' &&
    values.evaluationType !== 'Ratio' &&
    values.evaluationType !== 'RelativeDifference'
    ? 'IntegerNumber'
    : 'FloatingPoint';
});

watchEffect(() => {
  if (!checkLimits(values.min, values.max)) {
    setFieldError('max', t('Validation.InputRangeMinMax'));
    setFieldError('min', t('Validation.InputRangeMinMax'));
  }
});
</script>

<template>
  <Loader v-if="loadingFirstTime" type="content" />
  <template v-else>
    <div class="d-flex align-end">
      <EnumOrQuantityLabel :data="values" :label="$t('Quantity_one')" />

      <div v-if="loading" :style="{ transform: `scale(${0.5})` }" class="loadingIndicator">
        <VProgressCircular color="primary" indeterminate />
      </div>
    </div>

    <hr
      class="v-divider v-theme--ptwLightTheme text-primary-lighten-3 my-8"
      aria-orientation="horizontal"
    />

    <v-row class="mb-4">
      <v-col cols="5">
        <FormLabel :label="$t('Definition_one')" />
        <v-select
          v-if="numericEvaluationParameterTemplateDto?.availableDefinitions"
          :modelValue="
            numericEvaluationParameterTemplateDto.availableDefinitions?.find(
              (def) => def.id === values.definition?.id,
            )
          "
          :items="
            numericEvaluationParameterTemplateDto.availableDefinitions.map((v) => ({
              value: v,
              title: getDefinitonName(v),
            }))
          "
          @update:modelValue="(value) => setFieldValue('definition', value)"
        />
      </v-col>
      <v-col cols="2">
        <FormLabel :label="t('DefinitionUnit_one')">
          <p class="text-primary-darken-2">
            {{
              values.definition?.unitAbbreviation
                ? values.definition.unitAbbreviation
                : EMPTY_FIELD_SIGN
            }}
          </p>
        </FormLabel>
      </v-col>
      <v-col cols="2" class="pr-0">
        <FormLabel :label="t('ValueType')">
          <p class="text-primary-darken-2">
            {{ values.definition?.valueType ? $t(values.definition.valueType) : EMPTY_FIELD_SIGN }}
          </p>
        </FormLabel>
      </v-col>
      <v-col cols="2" class="pr-0">
        <FormLabel :label="t('DefinitionProtocol_one')">
          <p class="text-primary-darken-2">
            {{
              values.definition?.protocolId
                ? protocolsStore.getById(values.definition.protocolId)?.name
                : EMPTY_FIELD_SIGN
            }}
          </p>
        </FormLabel>
      </v-col>
    </v-row>

    <template
      v-if="
        values.definition &&
        values.definition.definitionParameterValueSets?.length &&
        values.definition.definitionParameterValueSets?.length > 0
      "
    >
      <VFieldLabel class="mt-3"
        >{{ $t('DefinitionParameterSet_other') }} ({{ $t('Required') }})</VFieldLabel
      >
      <br />

      <ParameterSetTable
        class="mt-3"
        :selectedDefinition="values.definition"
        :selectedValueSetId="values.definitionParameterValueSet?.id"
        :showCheckBoxes="true"
        @onDefinitionParameterValueSetSelected="
          (value: DefinitionParameterValueSetDto) => {
            setFieldValue('definitionParameterValueSet', value);
          }
        "
      />
    </template>

    <template v-if="values.definition && enableEntireForm">
      <hr
        class="v-divider v-theme--ptwLightTheme text-primary-lighten-3 my-8"
        aria-orientation="horizontal"
      />

      <v-row>
        <!-- Evaluation Type -->
        <v-col cols="4">
          <VeeSelect
            name="evaluationType"
            :placeholder="$t('Required')"
            :label="$t('EvaluationType_one')"
            :items="evaluationTypeOptions"
          />
        </v-col>
        <!-- Unit -->
        <v-col cols="2">
          <div class="d-flex">
            <FormLabel :label="$t('EvaluationUnit_one')">
              {{
                numericEvaluationParameterTemplateDto?.numericEvaluationParameter
                  ?.evaluationUnitAbbreviation || EMPTY_FIELD_SIGN
              }}
            </FormLabel>
            <InfoMessage :value="$t('NoteEvaluationUnitDerived')" :size="16" class="ml-1" />
          </div>
        </v-col>

        <!-- Decimal places -->
        <v-col cols="3">
          <VeeSelect
            v-if="values.definition.valueType === 'FloatingPoint'"
            :items="decimalsOptions"
            :label="$t('DisplayedDecimals')"
            name="decimalPlaces"
          />
        </v-col>
      </v-row>
    </template>
    <template v-if="values.definition && enableEntireForm">
      <hr
        class="v-divider v-theme--ptwLightTheme text-primary-lighten-3 my-8"
        aria-orientation="horizontal"
      />
      <v-row>
        <!-- Input  range-->
        <v-col cols="4">
          <VeeSelect
            v-if="values.definition"
            name="limitType"
            :label="$t('LimitTypeLabel')"
            :placeholder="$t('Required')"
            :items="[
              {
                title: $t('LimitType.Absolute'),
                value: EvaluationParameterLimitType.Absolute,
              },
              {
                title: $t('LimitType.WithoutEvaluation'),
                value: EvaluationParameterLimitType.WithoutEvaluation,
              },
            ]"
          />
        </v-col>
        <v-col v-if="values.definition" cols="8">
          <Range :label="$t('Range')" :disabled="false">
            <template #min>
              <VeeNumberField
                class="w-50"
                name="min"
                :prefix="$t('Min')"
                :suffix="
                  numericEvaluationParameterTemplateDto?.numericEvaluationParameter
                    ?.evaluationUnitAbbreviation
                "
                :format="{
                  valueType: computedValueType,
                }"
              />
            </template>

            <template #max>
              <VeeNumberField
                class="w-50"
                name="max"
                :prefix="$t('Max')"
                :suffix="
                  numericEvaluationParameterTemplateDto?.numericEvaluationParameter
                    ?.evaluationUnitAbbreviation
                "
                :format="{
                  valueType: computedValueType,
                }"
              />
            </template>
          </Range>
        </v-col>
      </v-row>
    </template>
  </template>
</template>

<style lang="scss" scoped>
.loadingIndicator {
  transform-origin: bottom;
  margin-bottom: 5px;
}
</style>
